<template>
  <div class="community-wizard">
    <!-- All Set Modal (overlay modal with backdrop) -->
    <all-set-modal
      v-if="showAllSetModal"
      @close="closeWizard"
      @start-tour="startTour"
    />

    <!-- Invites Sent Confirmation Modal (stays as modal) -->
    <invites-sent-modal
      :is-open="showInvitesSentModal"
      :sent-count="invitesSentCount"
      @close="onInvitesSentClose"
      @invite-more="reopenInviteModal"
    />

    <!-- Main wizard flow (steps + success + invite) -->
    <div v-if="!showInvitesSentModal && !showAllSetModal">
      <!-- Header with back button and step indicators -->
      <div class="wizard-header">
        <a class="back-link" @click="handleBack">
          <v-icon size="16" color="#9E9E9E">fa-regular fa-chevron-left</v-icon>
          Go Back to Communities
        </a>

        <!-- Hide stepper for Success and Invite views -->
        <div v-if="currentView === 'steps'" class="step-indicators">
          <div
            v-for="step in 3"
            :key="step"
            class="step-indicator"
            :class="{
              'step-active': currentStep === step,
              'step-completed': currentStep > step
            }"
          >
            <v-icon v-if="currentStep > step" size="18" color="white">fa-regular fa-check</v-icon>
            <span v-else>{{ step }}</span>
          </div>
        </div>
      </div>

      <!-- Two column layout -->
      <v-row no-gutters class="wizard-content">
        <!-- Left column - Form -->
        <v-col cols="12" md="6" class="form-column">
          <div class="form-container">
            <!-- Step content for steps 1-3 -->
            <component
              v-if="currentView === 'steps'"
              :is="currentStepComponent"
              v-model="formData"
              :loading="loading"
              @next="nextStep"
              @previous="previousStep"
            />

            <!-- Success view -->
            <success-modal
              v-else-if="currentView === 'success'"
              :community-id="createdCommunityId"
              @close="closeWizard"
              @open-invite-modal="openInviteModal"
              @show-all-set-modal="onShowAllSetModal"
            />

            <!-- Invite Members view -->
            <invite-members-modal
              v-else-if="currentView === 'invite'"
              :community-id="createdCommunityId"
              @close="closeWizard"
              @invites-sent="onInvitesSent"
            />
          </div>
        </v-col>

        <!-- Right column - Preview -->
        <v-col cols="12" md="6" class="preview-column">
          <div class="preview-container">
            <img
              v-if="currentPreviewImage"
              :src="currentPreviewImage"
              :alt="currentPreviewAlt"
              class="preview-image"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Loading overlay -->
      <loading-overlay v-if="loading" :message="loadingMessage" />
    </div>
  </div>
</template>

<script>
import clubService from '../../services/club.service'
import LoadingOverlay from '../LoadingOverlay.vue'
import SuccessModal from './SuccessModal.vue'
import InviteMembersModal from './InviteMembersModal.vue'
import InvitesSentModal from './InvitesSentModal.vue'
import AllSetModal from './AllSetModal.vue'

export default {
  name: 'CommunityWizard',
  components: {
    LoadingOverlay,
    SuccessModal,
    InviteMembersModal,
    InvitesSentModal,
    AllSetModal,
    Step1Basics: () => import('./steps/Step1Basics.vue'),
    Step2Branding: () => import('./steps/Step2Branding.vue'),
    Step3Confirmation: () => import('./steps/Step3Confirmation.vue')
  },
  props: {
    hideWizard: {
      type: Function,
      default: () => {}
    },
    isOwnerFlow: {
      type: Boolean,
      default: true // Default to owner flow
    }
  },
  data() {
    return {
      currentStep: 1,
      currentView: 'steps', // 'steps', 'success', or 'invite'
      maxStepReached: 1,
      loading: false,
      loadingMessage: '',

      formData: {
        name: '',
        description: '',
        imageFile: null,
        imagePreviewUrl: '',

        // Light mode colors
        primaryColorLight: '#28EA96',
        secondaryColorLight: '#005931',
        accentColorLight: '#F9E65F',

        // Dark mode colors
        primaryColorDark: '#28EA96',
        secondaryColorDark: '#C3FFE3',
        accentColorDark: '#FFC123',

        confirmChecked: false
      },

      createdCommunityId: null,
      showInvitesSentModal: false,
      showAllSetModal: false,
      invitesSentCount: 0
    }
  },
  computed: {
    currentStepComponent() {
      return `Step${this.currentStep}${this.getStepName(this.currentStep)}`
    },
    currentPreviewImage() {
      // Map current view and step to corresponding image
      if (this.currentView === 'success') {
        return require('../../assets/communitycreated.png')
      } else if (this.currentView === 'invite') {
        return require('../../assets/Invitemembers.png')
      } else if (this.currentView === 'steps') {
        const images = {
          1: require('../../assets/communitybasics.png'),
          2: require('../../assets/Brandyourcommunity.png'),
          3: require('../../assets/confirmation.png')
        }
        return images[this.currentStep]
      }
      return null
    },
    currentPreviewAlt() {
      // Provide descriptive alt text for accessibility
      if (this.currentView === 'success') {
        return 'Community created successfully'
      } else if (this.currentView === 'invite') {
        return 'Invite members to your community'
      } else if (this.currentView === 'steps') {
        const altTexts = {
          1: 'Community basics - name and description',
          2: 'Brand your community with colors and logo',
          3: 'Confirm community details'
        }
        return altTexts[this.currentStep]
      }
      return 'Community wizard preview'
    }
  },
  methods: {
    getStepName(step) {
      const names = {
        1: 'Basics',
        2: 'Branding',
        3: 'Confirmation'
      }
      return names[step] || ''
    },

    handleBack() {
      if (this.currentView === 'success' || this.currentView === 'invite') {
        this.closeWizard()
      } else if (this.currentStep > 1) {
        this.previousStep()
      } else {
        this.closeWizard()
      }
    },

    async nextStep() {
      if (this.currentStep === 3) {
        // Last step - create community
        await this.createCommunity()
      } else {
        this.currentStep++
        this.maxStepReached = Math.max(this.maxStepReached, this.currentStep)
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    async createCommunity() {
      this.loading = true
      this.loadingMessage = 'Creating your community...'

      try {
        const partnerId = this.$store.getters['broker/partnerId']

        if (!partnerId) {
          throw new Error('Partner ID not found. Please log in again.')
        }

        const communityData = {
          name: this.formData.name,
          description: this.formData.description,
          imageFile: this.formData.imageFile,
          primaryColorLight: this.formData.primaryColorLight,
          secondaryColorLight: this.formData.secondaryColorLight,
          accentColorLight: this.formData.accentColorLight,
          primaryColorDark: this.formData.primaryColorDark,
          secondaryColorDark: this.formData.secondaryColorDark,
          accentColorDark: this.formData.accentColorDark,
          partnerOwned: this.isOwnerFlow, // True if "I'll be the owner", false if "Assign someone else"
          partnerId
        }

        const club = await clubService.createCommunity(communityData)
        this.createdCommunityId = club.id

        // Emit success event
        this.$emit('community-created', club)

        // Show success view in wizard flow
        this.currentView = 'success'

      } catch (error) {
        console.error('Failed to create community:', error)
        // Show error message to user
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create community. Please try again.'
        alert(errorMessage) // TODO: Replace with better UI notification
        this.currentStep = 1 // Go back to first step
        this.currentView = 'steps'
      } finally {
        this.loading = false
      }
    },

    openInviteModal() {
      this.currentView = 'invite'
    },

    onInvitesSent(sentCount) {
      this.invitesSentCount = sentCount
      this.showInvitesSentModal = true
    },

    onInvitesSentClose() {
      this.showInvitesSentModal = false
      // Emit event to parent to show the AllSetModal
      // This allows the wizard to close and communities section to be visible
      this.$emit('show-all-set-modal')
    },

    onShowAllSetModal() {
      // Show the AllSetModal (used when user clicks "Omit for now" on SuccessModal)
      this.showAllSetModal = true
    },

    reopenInviteModal() {
      this.showInvitesSentModal = false
      this.currentView = 'invite'
    },

    startTour() {
      // Future implementation: launch product tour
      console.log('Starting community tour...')
      this.closeWizard()
    },

    closeWizard() {
      this.$emit('close')
    },

    checkOverflow() {
      this.$nextTick(() => {
        const contentEl = this.$el.querySelector('.form-column')
        if (contentEl) {
          const hasOverflow = contentEl.scrollHeight > contentEl.clientHeight
          const scrolledToBottom = hasOverflow &&
            (contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight <= 5)

          if (hasOverflow) {
            contentEl.classList.add('has-overflow')
          } else {
            contentEl.classList.remove('has-overflow')
          }

          if (scrolledToBottom) {
            contentEl.classList.add('scrolled-to-bottom')
          } else {
            contentEl.classList.remove('scrolled-to-bottom')
          }
        }
      })
    },

    handleScroll() {
      this.checkOverflow()
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkOverflow)

    // Add scroll listener to the form column and check overflow after DOM is ready
    this.$nextTick(() => {
      const formColumn = this.$el.querySelector('.form-column')
      if (formColumn) {
        formColumn.addEventListener('scroll', this.handleScroll)
      }

      // Longer delay to ensure child components are fully rendered
      setTimeout(() => {
        this.checkOverflow()
      }, 300)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow)

    const formColumn = this.$el.querySelector('.form-column')
    if (formColumn) {
      formColumn.removeEventListener('scroll', this.handleScroll)
    }
  },
  watch: {
    currentStep() {
      // Re-check overflow when step changes
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkOverflow()
        }, 300)
      })
    }
  },
  updated() {
    // Re-check when any component updates (child components finish rendering)
    this.checkOverflow()
  }
}
</script>

<style scoped lang="scss">
.community-wizard {
  height: 100vh;
  width: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  // Ensure wizard fills its container properly
  min-height: 100vh;
}

.wizard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 60px;
  flex-shrink: 0; // Prevent header from shrinking
  min-height: 80px; // Ensure consistent header height
  width: 100%;
  box-sizing: border-box;
}

.back-link {
  color: #9E9E9E !important;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #6B6B6B;
  }
}

.step-indicators {
  display: flex;
  gap: 12px;
  flex-shrink: 0; // Prevent stepper from shrinking
}

.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  border: 2px solid #e0e0e0;
  background: white;
  color: #9e9e9e;
  transition: all 0.3s;

  &.step-active {
    border-color: #28EA96;
    background: #28EA96;
    color: white;
  }

  &.step-completed {
    border-color: #28EA96;
    background: #28EA96;
    color: white;
  }
}

.wizard-content {
  flex: 1;
  overflow: hidden;
}

.form-column {
  padding: 60px !important;
  overflow-y: auto;
  height: calc(100vh - 80px); // Account for header height
  display: flex;
  flex-direction: column;
  // Changed from center to flex-start to prevent top content from being cropped
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;

  // Hide scrollbar completely
  &::-webkit-scrollbar {
    display: none !important;
  }

  // Firefox
  scrollbar-width: none !important;

  // IE and Edge
  -ms-overflow-style: none !important;

  // Fade gradient at bottom when scrollable
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 40px;
    background: linear-gradient(to bottom, transparent, white);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &.has-overflow:not(.scrolled-to-bottom)::after {
    opacity: 1;
  }
}

.form-container {
  max-width: 530px;
  width: 100%;
  // Auto margin to center vertically when there's enough space
  // but content stays at top when it overflows
  margin: auto 0;
}

.preview-column {
  background-color: white;
  height: calc(100vh - 80px); // Account for header height
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  // Same padding as header (60px on right) to align with stepper
  padding: 0 60px 0 0 !important;
}

.preview-container {
  height: 100%;
  max-width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.preview-image {
  max-width: 100%;
  max-height: calc(100vh - 80px); // Account for header height
  width: auto;
  height: auto;
  object-fit: contain;
  transition: opacity 0.3s ease;
  border-radius: 16px;
}

@media (max-width: 960px) {
  .preview-column {
    display: none;
  }

  .form-column {
    padding: 32px;
  }

  .form-container {
    max-width: 100%;
  }
}
</style>
